
import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { PasswordRules as PasswordRulesType } from '@/hooks/usePasswordValidation';

interface PasswordRulesProps {
  shouldShow: boolean;
  rules: PasswordRulesType;
  showPasswordsMatch?: boolean;
}

export function PasswordRules({ shouldShow, rules, showPasswordsMatch = false }: PasswordRulesProps) {
  return (
    <AnimatePresence>
      {shouldShow && (
        <motion.div 
          className="space-y-1 text-sm"
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          transition={{ duration: 0.2 }}
        >
          <p className="font-medium text-muted-foreground text-xs mb-1">Password requirements:</p>
          <ul className="space-y-1 text-xs">
            <li className="flex items-center gap-2">
              {rules.minLength ? (
                <Check className="h-3.5 w-3.5 text-green-500" />
              ) : (
                <X className="h-3.5 w-3.5 text-orange-500" />
              )}
              <span className={cn(rules.minLength ? "text-green-600" : "text-orange-600", "text-xs")}>
                At least 8 characters
              </span>
            </li>
            <li className="flex items-center gap-2">
              {rules.hasLower ? (
                <Check className="h-3.5 w-3.5 text-green-500" />
              ) : (
                <X className="h-3.5 w-3.5 text-orange-500" />
              )}
              <span className={cn(rules.hasLower ? "text-green-600" : "text-orange-600", "text-xs")}>
                At least one lowercase letter (a-z)
              </span>
            </li>
            <li className="flex items-center gap-2">
              {rules.hasUpper ? (
                <Check className="h-3.5 w-3.5 text-green-500" />
              ) : (
                <X className="h-3.5 w-3.5 text-orange-500" />
              )}
              <span className={cn(rules.hasUpper ? "text-green-600" : "text-orange-600", "text-xs")}>
                At least one uppercase letter (A-Z)
              </span>
            </li>
            <li className="flex items-center gap-2">
              {rules.hasNumber ? (
                <Check className="h-3.5 w-3.5 text-green-500" />
              ) : (
                <X className="h-3.5 w-3.5 text-orange-500" />
              )}
              <span className={cn(rules.hasNumber ? "text-green-600" : "text-orange-600", "text-xs")}>
                At least one number (0-9)
              </span>
            </li>
            <li className="flex items-center gap-2">
              {rules.hasSpecial ? (
                <Check className="h-3.5 w-3.5 text-green-500" />
              ) : (
                <X className="h-3.5 w-3.5 text-orange-500" />
              )}
              <span className={cn(rules.hasSpecial ? "text-green-600" : "text-orange-600", "text-xs")}>
                At least one special character (!@#$%^&amp;*(),.?&quot;:&#123;&#125;|&lt;&gt;)
              </span>
            </li>
            {showPasswordsMatch && (
              <li className="flex items-center gap-2">
                {rules.passwordsMatch ? (
                  <Check className="h-3.5 w-3.5 text-green-500" />
                ) : (
                  <X className="h-3.5 w-3.5 text-orange-500" />
                )}
                <span className={cn(rules.passwordsMatch ? "text-green-600" : "text-orange-600", "text-xs")}>
                  Passwords match
                </span>
              </li>
            )}
          </ul>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
